import React, { useState } from 'react';
import apiService from '../services/apiService';
import {
  ContentCard,
  CardTitle,
  FormGroup,
  ImageUpload,
  ImagePreview,
  ActionButtons,
  SaveButton,
  SuccessMessage,
  ErrorMessage
} from '../styles/AdminStyles';

const NewsPostEditor = ({ onPostCreated }) => {
  const [newsPostData, setNewsPostData] = useState({
    text: '',
    images: [],
    postDate: new Date().toISOString().split('T')[0] // Default to today's date
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);

  // Handle text change
  const handleTextChange = (e) => {
    setNewsPostData({
      ...newsPostData,
      text: e.target.value
    });
  };

  // Handle date change
  const handleDateChange = (e) => {
    setNewsPostData({
      ...newsPostData,
      postDate: e.target.value
    });
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles([...imageFiles, ...files]);
    
    // Process each file to get previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewsPostData(prev => ({
          ...prev,
          images: [...prev.images, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove image
  const removeImage = (index) => {
    setNewsPostData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Save news post
  const handleSave = async () => {
    if (!newsPostData.text.trim()) {
      setMessage({ type: 'error', text: 'Please enter post text.' });
      return;
    }

    if (newsPostData.images.length === 0) {
      setMessage({ type: 'error', text: 'Please upload at least one image.' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await apiService.createNewsPost(newsPostData);
      setMessage({ type: 'success', text: 'News post created successfully!' });
      
      // Reset form
      setNewsPostData({
        text: '',
        images: [],
        postDate: new Date().toISOString().split('T')[0]
      });
      setImageFiles([]);
      
      // Call the callback to refresh posts list if provided
      if (typeof onPostCreated === 'function') {
        onPostCreated();
      }
    } catch (error) {
      console.error('Error creating news post:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Error creating news post. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContentCard>
      <CardTitle>Create News Post</CardTitle>
      
      {message && (
        message.type === 'success' ? (
          <SuccessMessage>{message.text}</SuccessMessage>
        ) : (
          <ErrorMessage>{message.text}</ErrorMessage>
        )
      )}
      
      <FormGroup>
        <label>Post Date</label>
        <input
          type="date"
          value={newsPostData.postDate}
          onChange={handleDateChange}
        />
      </FormGroup>
      
      <FormGroup>
        <label>Post Text</label>
        <textarea
          value={newsPostData.text}
          onChange={handleTextChange}
          placeholder="Enter news post content"
          rows="5"
        />
      </FormGroup>
      
      <FormGroup>
        <label>Images</label>
        <ImageUpload onClick={() => document.getElementById('news-images').click()}>
          <input
            type="file"
            id="news-images"
            multiple
            accept="image/*"
            onChange={handleImageUpload}
          />
          <p>Click to upload images (multiple allowed)</p>
        </ImageUpload>
        
        {newsPostData.images.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <p>Image Previews:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {newsPostData.images.map((img, index) => (
                <ImagePreview key={index} style={{ position: 'relative', width: '150px' }}>
                  <img 
                    src={img} 
                    alt={`Preview ${index + 1}`} 
                    style={{ width: '100%', height: '100px', objectFit: 'cover' }} 
                  />
                  <button 
                    style={{
                      position: 'absolute',
                      top: '5px',
                      right: '5px',
                      background: 'rgba(255,0,0,0.7)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '25px',
                      height: '25px',
                      cursor: 'pointer'
                    }}
                    onClick={() => removeImage(index)}
                  >
                    ×
                  </button>
                </ImagePreview>
              ))}
            </div>
          </div>
        )}
      </FormGroup>
      
      <ActionButtons>
        <SaveButton onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Create Post'}
        </SaveButton>
      </ActionButtons>
    </ContentCard>
  );
};

export default NewsPostEditor;
